import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const CompetitionPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-purple-800 to-purple-900 text-white overflow-hidden">
      {/* Improved grid background with reduced opacity */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.05)_1px,transparent_1px),linear-gradient(rgba(255,255,255,.05)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/50 to-transparent" />
      
      {/* Back Button */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-8 left-8 z-10"
      >
        <Link to="/" className="flex items-center text-white hover:text-cyan-400">
          <ArrowLeft className="w-6 h-6 mr-2" />
          Back
        </Link>
      </motion.div>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 pt-24 pb-16">
        <motion.h1 
          className="text-6xl font-bold mb-12 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400">
            I Beg You: Please Write!
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-6 backdrop-blur-lg bg-purple-900/30 p-8 rounded-2xl border border-white/10"
        >
          <p className="text-lg leading-relaxed">
            North of 250,000 <em className="text-pink-300">books</em> are published every month. Millions of short stories are posted in journals and websites like Wattpad in the same time. They span every setting, genre, and writing style. If that wasn't enough, now with AI I can generate infinite stories about any subject in any style I want. And yet, I don't care.
          </p>
          
          <p className="text-lg leading-relaxed">
            The more abundant something is, the less valuable it feels. In a world where I can hear any story I want, tailored to my tastes, "quality" stops being a real differentiator. When everything is great, nothing is special.
          </p>
          
          <p className="text-lg leading-relaxed">
            To me, the only differentiator left is <em className="text-cyan-300">connection</em>. If a story, song, or piece of art comes from someone I know, like, and care about, experiencing it is 1000x more meaningful and exciting to me. In fact, I'd say I place an almost infinite premium on connection. I think this is generalizable — it's the reason people flock to Marvel movies even if for any given person, there's a better piece of media out there. When we have infinite AI generated music tailored to each person's ears, people will still flock to cultural schelling points they can connect over and sing together. I think we're basically here with short form writing already.
          </p>
          
          <p className="text-lg leading-relaxed">
            I really enjoy reading or listening to fiction in my leisure time, on my commutes, and while I exercise. And here I am, with infinite possible stories to read, and I don't feel very eager to choose any of them.
          </p>
          
          <p className="text-lg leading-relaxed">
            But you know what idea gets me excited? Reading something a friend wrote. The most memorable piece I've read lately was a short story by a friend. If you write something, I will read it, brainstorm about it, gush to others about it, tell the world how amazingly articulate and creative you are, and I might even pay you. So I beg you: please write!
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-8 backdrop-blur-xl bg-gradient-to-r from-pink-500/10 to-cyan-500/10 rounded-2xl border border-white/10"
        >
          <p className="text-lg leading-relaxed mb-6">
            We want to encourage those we share a connection with to finally write the stories sitting hidden in their hearts and heads. We are soliciting stories between 2500 words and 10000 words. We will share all valid stories publicly along with at least one review expressing everything we loved about each story. There will be a prize of at least $1000 distributed among the writers, depending on how many people participate. We have no genre preference: please share anything from literary fiction to Adam Sandler smut to hard scifi.
          </p>
          
          <motion.a
            href="https://docs.google.com/forms/d/e/1FAIpQLSeUTYAwgwB8Fkc6QK7oUg_3v4brnxlsWcQSQkAzJghkCeIDhg/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-gradient-to-r from-pink-500 to-cyan-500 text-white font-bold py-4 px-8 rounded-xl hover:opacity-90 transition-opacity text-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Submit Your Story
          </motion.a>
        </motion.div>
      </div>

      {/* Enhanced decorative elements */}
      <motion.div
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, 360]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute top-40 left-20 w-16 h-16 border-4 border-pink-500/30 rounded-full blur-sm"
      />
      <motion.div
        animate={{ 
          y: [0, 20, 0],
          rotate: [360, 0]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute bottom-40 right-40 w-20 h-20 border-4 border-cyan-500/30 rounded-lg blur-sm"
      />
      <motion.div
        animate={{ 
          y: [0, -15, 0],
          x: [0, 15, 0]
        }}
        transition={{ 
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
        className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-full blur-xl"
      />
    </div>
  );
};

export default CompetitionPage;
